"use client"
import React, { useState } from 'react'
import axios from 'axios'
import HeaderMenu from '@/app/components/HeaderMenu'
import { BlurFade } from '@/app/components/BlurFade'
import { StatefulButton } from '@/app/components/stateful-button'
import { motion, AnimatePresence } from "motion/react"
import { RedirectToMM } from '@/app/components/DeviceRedirect'
import type { Locale } from '@/lib/i18n'
import type { ContactDict } from '@/lib/i18n/dicts/contact'

// 服务承诺图标（与 dict.promises.items 顺序对应，纯装饰性 SVG 路径，不入字典）
const PROMISE_ICONS = [
    'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
    'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
    'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 00-1.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
]

// 联系方式（跨语言一致的联系数据，非文案，不入字典）
const PHONE_DISPLAY = '（+86）188 0038 2271'
const EMAIL_DISPLAY = 'vip@yygame.tech'

const inputCls = (invalid: boolean) =>
    `w-full rounded-lg bg-slate-950/60 ring-1 px-4 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition-all duration-300 focus:ring-2 focus:bg-slate-950 ${invalid
        ? 'ring-red-500 focus:ring-red-400'
        : 'ring-white/10 focus:ring-indigo-400'
    }`

interface Contact2ClientProps {
    dict: ContactDict
    locale: Locale
    // 页脚为服务端组件（读库统计），由服务端页面渲染后传入
    footer: React.ReactNode
}

export default function Contact2Client({ dict, locale, footer }: Contact2ClientProps) {
    const t = dict
    const [UserName, setUserName] = useState('')
    const [TelNumber, setTelNumber] = useState('')
    const [Company, setCompany] = useState('')
    const [Email, setEmail] = useState('')
    const [invalid, setInvalid] = useState({ name: false, tel: false })
    const [openFaq, setOpenFaq] = useState(-1)

    async function handleSubmit() {
        const nameEmpty = UserName.trim() === ''
        const telEmpty = TelNumber.trim() === ''
        if (nameEmpty || telEmpty) {
            setInvalid({ name: nameEmpty, tel: telEmpty })
            return
        }
        setInvalid({ name: false, tel: false })

        const reqBody = { Source: 'Web', Name: UserName, Phone: TelNumber, Email: Email, Company: Company }

        try {
            const response = await axios({
                method: 'post',
                url: '/api/sdk/ywq/AddContact',
                timeout: 5000,
                data: reqBody,
            })
            if (response.data.success == true) {
                alert(t.form.success)
            } else {
                alert(t.form.fail)
            }
        } catch (error) {
            // 与原页保持一致：网络异常静默
        }
    }

    return (
        <>
            <RedirectToMM href="/mm/contact" />
            <HeaderMenu locale={locale} />

            {/* Hero */}
            <section className="relative overflow-hidden bg-slate-950">
                <div className="pointer-events-none absolute -top-48 left-1/2 h-[520px] w-[820px] -translate-x-1/2 rounded-full bg-indigo-600/20 blur-[130px]" />
                <div className="pointer-events-none absolute top-24 left-[-120px] h-[300px] w-[300px] rounded-full bg-cyan-400/10 blur-[100px]" />

                <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-20 pb-14 text-center">
                    <BlurFade delay={0.05} inView>
                        <span className="inline-flex items-center gap-2 rounded-full ring-1 ring-indigo-400/30 bg-indigo-500/10 px-4 py-1.5 text-xs font-medium tracking-widest text-indigo-300 uppercase">
                            <span className="h-1.5 w-1.5 rounded-full bg-indigo-400 animate-pulse" />
                            {t.hero.badge}
                        </span>
                    </BlurFade>
                    <BlurFade delay={0.15} inView>
                        <h1 className="mt-6 text-4xl md:text-6xl font-black tracking-tight text-white">
                            {t.hero.titleA}
                            <span className="bg-gradient-to-r from-indigo-400 via-cyan-300 to-fuchsia-400 bg-clip-text text-transparent">{t.hero.titleB}</span>
                        </h1>
                    </BlurFade>
                    <BlurFade delay={0.25} inView>
                        <p className="mt-5 max-w-2xl mx-auto text-sm md:text-base leading-relaxed text-slate-400">
                            {t.hero.subtitle}
                        </p>
                    </BlurFade>
                </div>
            </section>

            {/* 表单 + 联系方式 */}
            <section className="bg-slate-950 py-16">
                <div className="max-w-7xl mx-auto px-4 md:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                        {/* 表单卡片 */}
                        <motion.div
                            initial={{ opacity: 0, y: 32 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.55 }}
                            className="lg:col-span-2 rounded-2xl bg-slate-900 ring-1 ring-white/10 p-7 md:p-9"
                        >
                            <h2 className="text-xl md:text-2xl font-bold text-white">{t.form.title}</h2>
                            <p className="mt-2 text-xs text-slate-500">{t.form.requiredTipPrefix} <span className="text-red-400">*</span> {t.form.requiredTipSuffix}</p>

                            <div className="mt-7 grid grid-cols-1 md:grid-cols-2 gap-5">
                                <div className="flex flex-col gap-1.5">
                                    <label className="text-sm font-medium text-slate-300">
                                        {t.form.name} <span className="text-red-400">*</span>
                                    </label>
                                    <input
                                        className={inputCls(invalid.name)}
                                        type="text"
                                        value={UserName}
                                        onChange={(e) => { setUserName(e.target.value); setInvalid((v) => ({ ...v, name: false })) }}
                                    />
                                    {invalid.name && <span className="text-xs text-red-400">{t.form.nameRequired}</span>}
                                </div>

                                <div className="flex flex-col gap-1.5">
                                    <label className="text-sm font-medium text-slate-300">
                                        {t.form.tel} <span className="text-red-400">*</span>
                                    </label>
                                    <input
                                        className={inputCls(invalid.tel)}
                                        type="text"
                                        value={TelNumber}
                                        onChange={(e) => { setTelNumber(e.target.value); setInvalid((v) => ({ ...v, tel: false })) }}
                                    />
                                    {invalid.tel && <span className="text-xs text-red-400">{t.form.telRequired}</span>}
                                </div>

                                <div className="flex flex-col gap-1.5">
                                    <label className="text-sm font-medium text-slate-300">{t.form.email}</label>
                                    <input
                                        className={inputCls(false)}
                                        type="text"
                                        value={Email}
                                        onChange={(e) => setEmail(e.target.value)}
                                    />
                                </div>

                                <div className="flex flex-col gap-1.5">
                                    <label className="text-sm font-medium text-slate-300">{t.form.company}</label>
                                    <input
                                        className={inputCls(false)}
                                        type="text"
                                        value={Company}
                                        onChange={(e) => setCompany(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="mt-8 flex justify-end">
                                <StatefulButton onClick={handleSubmit} className="animate-breathe">{t.form.submit}</StatefulButton>
                            </div>
                        </motion.div>

                        {/* 联系方式卡片 */}
                        <motion.div
                            initial={{ opacity: 0, y: 32 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.55, delay: 0.12 }}
                            className="rounded-2xl bg-gradient-to-b from-indigo-600/15 to-slate-900 ring-1 ring-indigo-400/20 p-7 flex flex-col"
                        >
                            <h2 className="text-base font-bold text-white">{t.direct.title}</h2>

                            <div className="mt-6 space-y-5">
                                <div className="flex items-start gap-3 group">
                                    <span className="rounded-xl bg-indigo-500/10 ring-1 ring-indigo-400/30 p-2.5 text-indigo-400">
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                                        </svg>
                                    </span>
                                    <div>
                                        <p className="text-xs text-slate-500">{t.direct.phoneLabel}</p>
                                        <p className="mt-0.5 text-sm font-bold text-slate-100">{PHONE_DISPLAY}</p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-3">
                                    <span className="rounded-xl bg-indigo-500/10 ring-1 ring-indigo-400/30 p-2.5 text-indigo-400">
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                                        </svg>
                                    </span>
                                    <div>
                                        <p className="text-xs text-slate-500">{t.direct.emailLabel}</p>
                                        <p className="mt-0.5 text-sm font-bold text-slate-100">{EMAIL_DISPLAY}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-8 flex-1 flex flex-col items-center justify-center rounded-xl bg-slate-950/50 ring-1 ring-white/5 p-6">
                                <p className="text-xs text-slate-400 mb-4">{t.direct.wechatTip}</p>
                                <img src="../img/wechat.png" alt={t.direct.wechatAlt} className="w-36 h-36 rounded-lg ring-1 ring-white/10 transition-transform duration-500 hover:scale-105" />
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* 服务承诺：与上下文同底色（slate-950），卡片提亮一档保持层次 */}
            <section className="bg-slate-950 py-16">
                <div className="max-w-7xl mx-auto px-4 md:px-8">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                        {t.promises.items.map((p, i) => (
                            <motion.div
                                key={p.title}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.5, delay: i * 0.1 }}
                                className="flex items-start gap-4 rounded-2xl bg-slate-900 ring-1 ring-white/10 p-6 transition-all duration-300 hover:ring-indigo-400/40 hover:-translate-y-1"
                            >
                                <span className="rounded-xl bg-indigo-500/10 ring-1 ring-indigo-400/30 p-3 text-indigo-400 shrink-0">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-6 w-6">
                                        <path strokeLinecap="round" strokeLinejoin="round" d={PROMISE_ICONS[i]} />
                                    </svg>
                                </span>
                                <div>
                                    <h3 className="text-base font-bold text-white">{p.title}</h3>
                                    <p className="mt-1.5 text-xs leading-5 text-slate-400">{p.desc}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* 常见问题 */}
            <section className="bg-slate-950 py-16">
                <div className="max-w-3xl mx-auto px-4 md:px-8">
                    <BlurFade inView>
                        <h2 className="text-center text-2xl md:text-3xl font-bold text-white">{t.faq.title}</h2>
                        <p className="mt-3 text-center text-sm text-slate-400">{t.faq.subtitle}</p>
                    </BlurFade>

                    <div className="mt-10 space-y-3">
                        {t.faq.items.map((faq, i) => {
                            const open = openFaq === i
                            return (
                                <motion.div
                                    key={faq.q}
                                    initial={{ opacity: 0, y: 16 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-40px" }}
                                    transition={{ duration: 0.4, delay: i * 0.08 }}
                                    className={`rounded-xl ring-1 overflow-hidden transition-colors duration-300 ${open ? 'bg-slate-900 ring-indigo-400/40' : 'bg-slate-900/60 ring-white/10 hover:ring-white/25'}`}
                                >
                                    <button
                                        onClick={() => setOpenFaq(open ? -1 : i)}
                                        className="w-full flex items-center justify-between px-6 py-4 text-left"
                                    >
                                        <span className="text-sm font-medium text-slate-100">{faq.q}</span>
                                        <motion.svg
                                            xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"
                                            className="h-4 w-4 shrink-0 text-indigo-400"
                                            animate={{ rotate: open ? 180 : 0 }}
                                            transition={{ duration: 0.3 }}
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                                        </motion.svg>
                                    </button>
                                    <AnimatePresence initial={false}>
                                        {open && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: 'auto', opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.35, ease: 'easeInOut' }}
                                            >
                                                <p className="px-6 pb-5 text-xs leading-6 text-slate-400">{faq.a}</p>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </motion.div>
                            )
                        })}
                    </div>
                </div>
            </section>

            {footer}
        </>
    )
}
